import { useEffect, useState, useRef } from 'react';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import axiosInstance from '../../../../lib/axios';
import checkAndLock from '../../../../GlobalFunction/checkAndLock';
import UnLock from '../../../../GlobalFunction/UnLock';
import CrudButtons from '../../../../GlobalFunction/CrudButtons';
import ReportTable from '../../../../atoms/ReportFolder/ReportTable';
import { useReactToPrint } from 'react-to-print';

const columns = [
  { id: 'RowNum', label: 'S.No.', minWidth: 170 },
  { id: 'VndClsName', label: 'Vendor Class Name', minWidth: 100 },
  { id: 'VndClsAbrv', label: 'Abrv', minWidth: 100 },
  { id: 'Remark', label: 'Remark', minWidth: 100 },
];

const ClassMaster = () => {
  const [formData, setFormData] = useState({
    VndClsName: '',
    VndClsAbrv: '',
    Remark: '',
  });

  const [error, setError] = useState({
    VndClsName: false,
    VndClsAbrv: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [currentVndClsId, setCurrentVndClsId] = useState(null);
  const [mode, setMode] = useState('view');
  const [status, setStatus] = useState("1");
  const [lastInsertedVndClsId, setLastInsertedVndClsId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [userId] = useState(sessionStorage.getItem('UserId'));
  const [dataForPrint, setDataForPrint] = useState({});
  const [rows, setRows] = useState([]);

  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef });
  const vndClsNameRef = useRef(null);
  const vndClsAbrvRef = useRef(null);
  const remarkRef = useRef(null);

  useEffect(() => {
    if (location.state && location.state.VndClsId) {
      setCurrentVndClsId(location.state.VndClsId);
      fetchVendorClassData(location.state.VndClsId, "R");
      setMode('view');
    } else {
      setMode('add');
      setIsFormDisabled(false);
    }
  }, [location]);

  const fetchVendorClassData = async (id, flag) => {
    try {
      const response = await axiosInstance.post(`MstVendClass/RetriveMstVndCls`, {
        VndClsId: parseInt(id),
        Flag: flag,
      });

      if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
        const vendorData = response.data.Data[0];
        setFormData({
          VndClsName: vendorData.VndClsName || '',
          VndClsAbrv: vendorData.VndClsAbrv || '',
          Remark: vendorData.Remark || '',
        });
        setStatus(vendorData.status?.toString() || "1"); // Default to "1" if status is undefined
        setIsFormDisabled(true);
        setCurrentVndClsId(vendorData.VndClsId);
      } else if (response.data.Status === 1 && response.data.responseStatusCode === 2) {
        toast.info(response.data.Message);
      } else {
        toast.info('No record found.');
      }
    } catch (error) {
      toast.error('Error fetching vendor class data. Please try again.');
    }
  };

  useEffect(() => {
    const getRow = async () => {
      const params = {
        start: 0,
        PageSize: 10,
        SearchText: "",
      };
      try {
        const res = await axiosInstance.post('MstVendClass/GetMstVndClsDashBoard', { params });
        if (res.data && res.data.Data) {
          setRows(res.data.Data);
          setDataForPrint(res.data.Data);
        }
      } catch (err) {
        toast.error('Error fetching data:', err);
      }
    };
    getRow();
  }, []);

  const handlePrevious = async () => {
    if (currentVndClsId && currentVndClsId > 1) {
      await fetchVendorClassData(currentVndClsId, "P");
    }
  };

  const handleNext = async () => {
    if (currentVndClsId) {
      await fetchVendorClassData(currentVndClsId, "N");
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeStatus = (event) => {
    const updatedStatus = event.target.checked ? "1" : "0";
    setStatus(updatedStatus);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (event.target === vndClsNameRef.current) {
        vndClsAbrvRef.current.focus();
      } else if (event.target === vndClsAbrvRef.current) {
        remarkRef.current.focus();
      }
    }
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!formData.VndClsName) {
      toast.error('Vendor Class Name is required');
      setError((prev) => ({ ...prev, VndClsName: true }));
      hasError = true;
    } else {
      setError((prev) => ({ ...prev, VndClsName: false }));
    }

    if (hasError) return;

    try {
      const payload = {
        VndClsName: formData.VndClsName,
        VndClsAbrv: formData.VndClsAbrv,
        Remark: formData.Remark,
        status: status,
      };

      let response;
      if (mode === 'edit') {
        payload.VndClsId = currentVndClsId;
        payload.UpdatedBy = parseInt(userId, 10);
        response = await axiosInstance.patch(`MstVendClass/UpdateMstVndCls`, payload);
      } else {
        payload.CreatedBy = parseInt(userId, 10);
        response = await axiosInstance.post(`MstVendClass/InsertMstVndCls`, payload);
      }

      if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
        toast.success(response.data.Message);
        if (mode === 'add') {
          setLastInsertedVndClsId(response.data.Data);
          await fetchVendorClassData(response.data.Data, "R");
          setMode('view');
          setIsFormDisabled(true);
          setCurrentVndClsId(response.data.Data);
        } else {
          setMode('view');
          setIsFormDisabled(true);
          await UnLock(currentVndClsId, "MstVndcls", "VndClsId"); // Unlock after successful edit
        }
      } else {
        toast.error(response.data.Message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Error saving/updating vendor class. Please try again.');
    }
  };

  const handleEdit = async () => {
    let flag = await checkAndLock(currentVndClsId, "MstVndcls", "VndClsId");
    switch (flag) {
      case 0:
        setMode("edit");
        setIsFormDisabled(false);
        break;
      case 1:
        toast.info("Record Already Locked", { autoClose: 500 });
        break;
      case 2:
        toast.error("Record Failed To Lock", { autoClose: 500 });
        break;
      default:
        toast.error("Something Went Wrong", { autoClose: 500 });
    }
  };

  const handleAdd = () => {
    setMode('add');
    setIsFormDisabled(false);
    setFormData({
      VndClsName: '',
      VndClsAbrv: '',
      Remark: '',
    });
    setCurrentVndClsId(null);
    setStatus("1");

    setTimeout(() => {
      vndClsNameRef.current?.focus();
    }, 0);
  };

  const handleCancel = async () => {
    try {
      await fetchVendorClassData(1, "L");
      await UnLock(currentVndClsId, "MstVndcls", "VndClsId");
      setStatus("1");
      setMode('view');
      setIsFormDisabled(true);
    } catch (error) {
      toast.error('Error occurred while cancelling. Please try again.');
    }
  };

  const handleExit = () => {
    navigate('/classtable');
  };

  const handleDeleteClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmDelete = async () => {
    const flag = await checkAndLock(currentVndClsId, "MstVndcls", "VndClsId");
    switch (flag) {
      case 0:
        setOpenConfirmDialog(false);
        await fetchVendorClassData(currentVndClsId, "D");
        await fetchVendorClassData(currentVndClsId, "N");
        setStatus("1");
        setMode('view');
        setIsFormDisabled(true);
        break;
      case 1:
        toast.info("Record Already Locked", { autoClose: 500 });
        break;
      case 2:
        toast.error("Record Failed To Lock", { autoClose: 500 });
        break;
      default:
        toast.error("Something Went Wrong", { autoClose: 500 });
    }
  };

  return (
    <>
      <Box className="form-container" sx={{ height: { xs: '700px', sm: '550px' } }}>
        <ToastContainer />
        <Grid container spacing={2} className="form_grid">
          <Grid
            item
            xs={12}
            className="form_title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
              flexDirection: { xs: 'column', sm: 'row', md: 'row' },
            }}
          >
            <Grid
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                marginBottom: { xs: '10px', sm: 0 },
              }}
            >
              <Button
                variant="contained"
                size="small"
                className="three-d-button-previous"
                onClick={handlePrevious}
                sx={{ backgroundColor: '#635BFF', marginRight: { xs: '5px', sm: '10px' }, display: { xs: 'none', sm: 'block' } }}
                disabled={mode !== 'view' || !currentVndClsId || currentVndClsId === 1}
              >
                <KeyboardArrowLeftIcon />
              </Button>
              <Button
                variant="contained"
                size="small"
                className="three-d-button-next"
                onClick={handleNext}
                sx={{ backgroundColor: '#635BFF', display: { xs: 'none', sm: 'block' } }}
                disabled={mode !== 'view' || !currentVndClsId}
              >
                <NavigateNextIcon />
              </Button>
            </Grid>

            <Typography
              variant="h5"
              sx={{
                margin: 'auto',
                textAlign: { xs: 'center', sm: 'center' },
                fontSize: { xs: '24px', sm: '18px', md: '24px' },
              }}
            >
              Vendor Class Master
            </Typography>

            <Grid sx={{ display: 'flex', justifyContent: 'end' }}>
              <CrudButtons
                moduleName="Class Master"
                mode={mode}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onView={handlePrint}
                onDelete={handleDeleteClick}
                onExit={handleExit}
                onPrevious={handlePrevious}
                onNext={handleNext}
                readOnlyMode={isFormDisabled}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} className="form_field">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  id="VndClsName"
                  name="VndClsName"
                  label={
                    <span>
                      Vendor Class <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  variant="filled"
                  fullWidth
                  className="custom-textfield"
                  value={formData.VndClsName}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  error={error.VndClsName}
                  helperText={error.VndClsName && ""}
                  inputRef={vndClsNameRef}
                  onKeyDown={handleKeyPress}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  id="VndClsAbrv"
                  name="VndClsAbrv"
                  label="Abrv"
                  variant="filled"
                  fullWidth
                  className="custom-textfield"
                  value={formData.VndClsAbrv}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  inputRef={vndClsAbrvRef}
                  onKeyDown={handleKeyPress}
                />
              </Grid>

              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  id="Remark"
                  name="Remark"
                  label="Remark"
                  variant="filled"
                  fullWidth
                  className="custom-textfield"
                  value={formData.Remark}
                  onChange={handleInputChange}
                  disabled={isFormDisabled}
                  inputRef={remarkRef}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={2}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isFormDisabled}
                        checked={status === "1"}
                        onChange={handleChangeStatus}
                      />
                    }
                    label="Status"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className="form_button">
            {mode === 'view' && (
              <>
                <Button
                  variant="contained"
                  sx={{ mr: 1, background: 'linear-gradient(290deg, #d4d4d4, #ffffff)' }}
                  onClick={handleAdd}
                  disabled
                >
                  Submit
                </Button>
                <Button
                  variant="contained"
                  sx={{ mr: 1, background: 'linear-gradient(290deg, #a7c5e9, #ffffff)' }}
                  onClick={handleEdit}
                  disabled
                >
                  Cancel
                </Button>
              </>
            )}
            {(mode === 'edit' || mode === 'add') && (
              <>
                <Button variant="contained" sx={{ mr: 1 }} onClick={handleSubmit}>
                  Submit
                </Button>
                <Button variant="contained" sx={{ mr: 1 }} onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Box>

      <div style={{ display: 'none' }}>
        {Object.keys(dataForPrint).length > 0 && (
          <ReportTable
            columns={columns}
            rows={rows}
            title="Vendor Class Master"
            contentRef={contentRef}
            customStyles={{ backgroundColor: '#f5f5f5' }}
          />
        )}
      </div>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              backgroundColor: '#635bff',
              color: 'white',
              '&:hover': { backgroundColor: '#1565c0', color: 'white' },
            }}
            onClick={handleConfirmDelete}
          >
            Yes
          </Button>
          <Button
            sx={{
              backgroundColor: '#635bff',
              color: 'white',
              '&:hover': { backgroundColor: '#1565c0', color: 'white' },
            }}
            onClick={handleCloseConfirmDialog}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClassMaster;